'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getOrganizations } from '@/repositories/organizationsRepository'
import { useClientIdentityVersion } from '@/hooks/useClientIdentityVersion'
import { withAnalyticsTracking } from '@/utilities/analytics/apiAnalytics'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { resolveOrgProgramBootstrapPartnerId } from '@/utilities/dashboard/orgProgramSwitcherTree'
import type { OrgSchemaType } from '@/types/portal-api.generated'

export const ORG_PROGRAM_TREE_QUERY_KEY = ['org-program-tree'] as const

function normalizeOrgLoadError(error: unknown): Error {
  const fallbackMessage = 'Failed to load organizations'

  if (error instanceof Error) return error

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return new Error(error.message, { cause: error })
  }

  switch (typeof error) {
    case 'string': {
      const message = error.length > 0 ? error : fallbackMessage
      return new Error(message, { cause: error })
    }
    case 'number':
    case 'boolean':
    case 'bigint':
      return new Error(String(error), { cause: error })
    case 'symbol':
      return new Error(error.description || fallbackMessage, { cause: error })
    default:
      return new Error(fallbackMessage, { cause: error })
  }
}

async function fetchOrganizations(): Promise<OrgSchemaType[]> {
  try {
    const { data } = await withAnalyticsTracking(
      async () => {
        const response = await getOrganizations()
        if (response.error) {
          throw normalizeOrgLoadError(response.error)
        }
        return response
      },
      {
        eventName: 'client:partner_list:organizations_api',
        additionalProperties: {
          source: 'org_program_tree',
        },
        getSuccessProperties: (response) => ({
          organizationCount: response.data?.length ?? 0,
        }),
      },
    )

    return data ?? []
  } catch (error) {
    trackEvent('client:partner_list:orgs_load_error', {
      source: 'org_program_tree',
      fallback: 'client_portal_org_program_context',
      ...errorAnalyticsPayload(error),
    })
    throw error
  }
}

export function useOrgProgramTree({
  enabled = true,
  selectedClientId,
  allowedPartnerIds,
}: {
  enabled?: boolean
  selectedClientId?: string | null
  allowedPartnerIds?: readonly string[]
} = {}): {
  rawOrganizations: OrgSchemaType[]
  bootstrapPartnerId: string | null
  fetchSettled: boolean
  loadFailed: boolean
  loading: boolean
} {
  const currentIdentityVersion = useClientIdentityVersion()
  const query = useQuery({
    queryKey: [...ORG_PROGRAM_TREE_QUERY_KEY, currentIdentityVersion] as const,
    // Organizations are session context. Loading them once here lets a bare
    // dashboard resolve the exact primary/program partner before flags load.
    enabled,
    retry: false,
    queryFn: fetchOrganizations,
  })

  const rawOrganizations = useMemo(() => query.data ?? [], [query.data])
  const bootstrapPartnerId = useMemo(
    () =>
      query.isSuccess
        ? resolveOrgProgramBootstrapPartnerId(rawOrganizations, selectedClientId, allowedPartnerIds)
        : null,
    [allowedPartnerIds, query.isSuccess, rawOrganizations, selectedClientId],
  )
  return {
    rawOrganizations,
    bootstrapPartnerId,
    fetchSettled: enabled && (query.isSuccess || query.isError),
    loadFailed: enabled && query.isError,
    loading: enabled && query.isPending,
  }
}
